import { useCallback, useRef, useState } from "react";
import { ChatMessage } from "../../../interfaces/chatbotInterface";
import { getMoreSuggestedQueries } from "../../../api/chatbot";
import { Box, CircularProgress, Fab, Stack, Tooltip } from "@mui/material";
import styles from "../styles";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { NON_EXISTING } from "../../../utility/constants";
import ChatMessageItem from "./chat-message-item";

type ChatMessagesProps = {
  sessionUuid: string;
  messages: ChatMessage[];
  onSend: (text?: string) => void;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
};

const ChatMessages = ({
  sessionUuid,
  messages,
  onSend,
  messagesEndRef,
  loading,
  hasMore,
  onLoadMore,
  setMessages,
}: ChatMessagesProps) => {
  const messagesWrapperRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Intersection observer for oldest message
  const oldestMessageRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!hasMore || loading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [hasMore, loading, onLoadMore]
  );

  const handleScroll = () => {
    if (!messagesWrapperRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesWrapperRef.current;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    setShowScrollButton(distanceFromBottom >= 100);
  };

  const scrollToBottom = () => {
    requestAnimationFrame(() =>
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    );
  };

  const handleSuggestedClick = async (q: string) => {
    if (q !== "See more sample queries") {
      onSend(q);
      return;
    }

    const { queries, hasMore } = await getMoreSuggestedQueries();
    setMessages((prev) => {
      const lastMessage = prev[prev.length - 1];
      const isMessageFromSuggestion = lastMessage?.type === "options";

      if (isMessageFromSuggestion) {
        const suggestedQueries = lastMessage.suggestedQueries ?? [];
        const updatedLast = {
          ...lastMessage,
          suggestedQueries: [
            ...suggestedQueries.filter((sq) => sq !== "See more sample queries"),
            ...queries,
            ...(hasMore ? ["See more sample queries"] : []),
          ],
        };
        return [...prev.slice(0, -1), updatedLast];
      }

      return [
        ...prev,
        {
          recordId: NON_EXISTING,
          sessionUuid,
          senderType: "ai",
          content: "Here are more sample queries you can try:",
          isReserved: false,
          type: "options",
          suggestedQueries: [...queries, ...(hasMore ? ["See more sample queries"] : [])],
        },
      ];
    });

    scrollToBottom();
  };

  return (
    <Box ref={messagesWrapperRef} sx={styles.messagesWrapper} onScroll={handleScroll}>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      <Stack spacing={2} sx={styles.messagesInner}>
        {messages.map((msg, idx) => (
          <ChatMessageItem
            key={idx}
            message={msg}
            isOldest={idx === 0}
            oldestMessageRef={oldestMessageRef}
            onSuggestedClick={handleSuggestedClick}
          />
        ))}
        <div ref={messagesEndRef} />
      </Stack>

      {showScrollButton && (
        <Tooltip title="Scroll to latest" placement="top">
          <Fab
            size="small"
            color="primary"
            sx={{ position: "absolute", bottom: 80, right: 16 }}
            onClick={scrollToBottom}
          >
            <ArrowDownwardIcon />
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
};

export default ChatMessages;